// Подпись запроса устройством (ADR-005, §2 документа 12, ADR-031).
//
// Канон и порядок склейки зафиксированы векторами `conformance/signing/vectors.json`: сервер, TS-ядро и
// Kotlin-ядро обязаны получать одни и те же байты. Расхождение на байт означает, что клиент просто не
// войдёт, а разбираться в этом дорого, — поэтому здесь ничего нельзя «немного упростить».

import { concatBytes, toBase64Url } from "./bytes.ts";
import type { Bytes } from "./bytes.ts";
import type { Clock, RandomSource } from "./platform.ts";

/** Имена заголовков подписи (`conformance/signing/vectors.json` → `headers`). */
export const signatureHeaders = {
  client: "X-Yofis-Client",
  device: "X-Yofis-Device",
  nonce: "X-Yofis-Nonce",
  signature: "X-Yofis-Signature",
  timestamp: "X-Yofis-Timestamp",
} as const;

/** Алгоритмы ключа устройства: Ed25519 основной, P-256 — запасной там, где Ed25519 нет (ADR-031). */
export type KeyAlgorithm = "ED25519" | "ECDSA_P256";

/** Ключ устройства. Приватная часть не покидает хранилище платформы, наружу — только подпись. */
export interface DeviceSigner {
  readonly algorithm: KeyAlgorithm;
  /** Подпись канона: Ed25519 — 64 байта, P-256 — raw `r‖s` (64 байта, IEEE P1363), не DER. */
  sign(canonical: Bytes): Promise<Bytes>;
  /** Публичный ключ: Ed25519 — 32 байта, P-256 — несжатая точка SEC1 (65 байт, начинается с 0x04). */
  publicKey(): Promise<Bytes>;
}

/** Что подписываем. */
export interface SignedRequest {
  /** Путь Connect, например `/yofis.v1.UserService/GetMe`. */
  methodPath: string;
  /** Секунды Unix UTC. */
  timestamp: number;
  /** Строка base64url из 16 случайных байт (22 символа). */
  nonce: string;
  /** Байты тела ровно так, как они уйдут в сеть; пустое тело — пустой массив. */
  body: Bytes;
}

const encoder = new TextEncoder();

/**
 * canonical = method_path ‖ timestamp ‖ nonce ‖ body — без разделителей.
 * timestamp — десятичные секунды, nonce — строка base64url (именно строка, а не её байты после разбора).
 */
export function canonicalBytes(request: SignedRequest): Bytes {
  return concatBytes(
    encoder.encode(request.methodPath),
    encoder.encode(String(request.timestamp)),
    encoder.encode(request.nonce),
    request.body,
  );
}

/** Новый nonce: 16 случайных байт в base64url — 22 символа без паддинга. */
export function newNonce(random: RandomSource): string {
  return toBase64Url(random.bytes(16));
}

/** Заголовки подписанного запроса. */
export interface SignatureHeaders {
  [name: string]: string;
}

/** Считает подпись и собирает заголовки. `deviceId` — идентификатор устройства из сессии. */
export async function signRequest(
  signer: DeviceSigner,
  deviceId: string,
  request: SignedRequest,
): Promise<SignatureHeaders> {
  const signature = await signer.sign(canonicalBytes(request));
  return {
    [signatureHeaders.device]: deviceId,
    [signatureHeaders.timestamp]: String(request.timestamp),
    [signatureHeaders.nonce]: request.nonce,
    [signatureHeaders.signature]: toBase64Url(signature),
  };
}

/** Секунды Unix UTC по часам платформы: окно сервера ±300 с, поэтому часы — общая зависимость. */
export function timestampSeconds(clock: Clock): number {
  return Math.floor(clock.now() / 1000);
}
